import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * A custom element that shows one product image per colour.
 *
 * It deliberately holds no variant state of its own. Clicking a tile checks the matching
 * input in the theme's own variant picker and dispatches a `change` event, so the existing
 * variant logic (URL, price, media gallery, buy buttons) does the work. Without JavaScript
 * each tile stays a plain link to the variant, so the block keeps working.
 *
 * @extends {Component<{}>}
 */
class ColorImageSwatches extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handleClick);

    const section = this.closest('.shopify-section, dialog');
    if (!section) return;
    section.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#handleClick);

    const section = this.closest('.shopify-section, dialog');
    if (!section) return;
    section.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * The variant picker this block drives — the one in the same section.
   * @returns {Element | null}
   */
  get #variantPicker() {
    const scope = this.closest('.shopify-section, dialog') ?? document;
    return scope.querySelector('variant-picker');
  }

  /**
   * Selects a colour by driving the theme's variant picker.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const tile = event.target.closest('[data-option-value-id]');
    if (!(tile instanceof HTMLElement)) return;

    const optionValueId = tile.dataset.optionValueId;
    if (!optionValueId) return;

    // Already the selected colour: swallow the click instead of reloading the page.
    if (tile.getAttribute('aria-current') === 'true') {
      event.preventDefault();
      return;
    }

    const picker = this.#variantPicker;
    // No picker on the page (or it was removed): let the link navigate to the variant.
    if (!picker) return;

    const input = picker.querySelector(`input[data-option-value-id="${optionValueId}"]`);

    if (input instanceof HTMLInputElement) {
      event.preventDefault();
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    // The picker can be rendered as dropdowns instead of buttons.
    const option = picker.querySelector(`option[data-option-value-id="${optionValueId}"]`);

    if (option instanceof HTMLOptionElement && option.parentElement instanceof HTMLSelectElement) {
      event.preventDefault();
      const select = option.parentElement;
      select.value = option.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  /**
   * Updates the label and the selected tile after a variant change.
   * @param {Event & { promise?: Promise<any> }} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (!event.promise) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const source = html.querySelector(`color-image-swatches[data-block-id="${this.dataset.blockId}"]`);
        if (!source) return;

        const currentTiles = Array.from(this.querySelectorAll('[data-option-value-id]'));
        const newTiles = Array.from(source.querySelectorAll('[data-option-value-id]'));

        const sameColours =
          currentTiles.length === newTiles.length &&
          currentTiles.every(
            (tile, index) =>
              /** @type {HTMLElement} */ (tile).dataset.optionValueId ===
              /** @type {HTMLElement} */ (newTiles[index]).dataset.optionValueId
          );

        // A different set of colours means a different product: take the new markup wholesale.
        if (!sameColours) {
          this.replaceChildren(...Array.from(source.childNodes).map((node) => document.importNode(node, true)));
          return;
        }

        const label = this.querySelector('.color-image-swatches__label');
        const newLabel = source.querySelector('.color-image-swatches__label');

        if (label && newLabel) {
          label.replaceWith(document.importNode(newLabel, true));
        } else if (label && !newLabel) {
          label.remove();
        } else if (!label && newLabel) {
          this.prepend(document.importNode(newLabel, true));
        }

        currentTiles.forEach((tile, index) => {
          const newTile = /** @type {HTMLElement} */ (newTiles[index]);
          if (!(tile instanceof HTMLElement)) return;

          if (newTile.getAttribute('aria-current') === 'true') {
            tile.setAttribute('aria-current', 'true');
          } else {
            tile.removeAttribute('aria-current');
          }

          tile.dataset.available = newTile.dataset.available ?? 'true';

          const href = newTile.getAttribute('href');
          if (href) tile.setAttribute('href', href);
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[color-image-swatches] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('color-image-swatches')) {
  customElements.define('color-image-swatches', ColorImageSwatches);
}
